import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAuditLogs, AuditLogEntry } from '../../api/services/auditLogService';
import AdminTable, { AdminTableColumn, StatusBadge } from '../../components/AdminTable';

const ACTION_LABELS: Record<string, string> = {
  'user.role_change': 'Changement de rôle',
  'user.suspend': 'Suspension de compte',
  'user.reactivate': 'Réactivation de compte',
  'user.verify': 'Vérification de compte',
  'user.unverify': 'Retrait de vérification',
  'vehicle.suspend': 'Suspension de véhicule',
  'vehicle.reactivate': 'Réactivation de véhicule',
  'vehicle.delete': 'Suppression de véhicule',
  'vehicle.update': "Modification d'annonce",
  'vehicle.reassign': "Transfert d'annonce",
  'verification.approve': 'Vérification approuvée',
  'verification.reject': 'Vérification rejetée',
  'ticket.status_open': 'Ticket rouvert',
  'ticket.status_in_progress': 'Ticket pris en charge',
  'ticket.status_resolved': 'Ticket résolu',
  'ticket.status_closed': 'Ticket fermé',
  'payment.status_refunded': 'Paiement remboursé',
  'review.delete': "Suppression d'avis",
};

const TARGET_LABELS: Record<string, string> = {
  user: 'Utilisateur',
  vehicle: 'Véhicule',
  verification: 'Vérification',
  ticket: 'Ticket',
  payment: 'Paiement',
  review: 'Avis',
};

const actionTone = (action: string): 'ok' | 'warn' | 'bad' | 'neutral' => {
  if (action.includes('suspend') || action.includes('reject') || action.includes('delete')) return 'bad';
  if (action.includes('reactivate') || action.includes('approve') || action.includes('verify')) return 'ok';
  return 'neutral';
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ManagementAuditLog = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    getAuditLogs(token)
      .then(setLogs)
      .catch((err: any) => setError(err.message || 'Erreur lors du chargement du journal'))
      .finally(() => setLoading(false));
  }, [token]);

  const targetTypes = useMemo(() => Array.from(new Set(logs.map((l) => l.target_type))), [logs]);

  const filtered = useMemo(() => {
    if (actionFilter === 'all') return logs;
    return logs.filter((l) => l.target_type === actionFilter);
  }, [logs, actionFilter]);

  const columns: AdminTableColumn<AuditLogEntry>[] = [
    {
      key: 'action',
      label: 'Action',
      render: (l) => (
        <StatusBadge status={ACTION_LABELS[l.action] || l.action} tone={actionTone(l.action)} />
      )
    },
    {
      key: 'target',
      label: 'Cible',
      render: (l) => (
        <span className="text-xs text-gray-600">
          {TARGET_LABELS[l.target_type] || l.target_type}
          {l.target_id && <span className="font-mono text-gray-400"> · {l.target_id.slice(0, 8)}</span>}
        </span>
      )
    },
    {
      key: 'actor',
      label: 'Effectué par',
      render: (l) => (
        <span className="text-sm text-gray-800">
          {l.actor_first_name ? `${l.actor_first_name} ${l.actor_last_name}` : (l.actor_email || '—')}
        </span>
      )
    },
    {
      key: 'details',
      label: 'Détails',
      render: (l) => <span className="text-xs text-gray-500 max-w-xs truncate block">{l.details || '—'}</span>
    },
    { key: 'created_at', label: 'Date', render: (l) => <span className="text-xs text-gray-500">{formatDateTime(l.created_at)}</span> },
  ];

  return (
    <main className="flex-1 p-4 lg:p-6 space-y-5 overflow-y-auto bg-gray-50">
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-[18px] font-semibold text-gray-900">Journal d'audit ({filtered.length})</h2>
            <p className="text-[12.5px] text-gray-500 mt-0.5">Actions sensibles effectuées depuis le back-office</p>
          </div>
          {targetTypes.length > 0 && (
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">Toutes les cibles</option>
              {targetTypes.map((t) => (
                <option key={t} value={t}>{TARGET_LABELS[t] || t}</option>
              ))}
            </select>
          )}
        </div>

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        <AdminTable
          columns={columns}
          rows={filtered}
          loading={loading}
          rowKey={(l) => l.id_log}
          emptyLabel="Aucune action enregistrée pour l'instant"
        />
      </div>
    </main>
  );
};

export default ManagementAuditLog;
